import { useMemo } from "react";
import { Checkbox } from "@mui/material";
import { Clinic } from "../../models";

// Clinics without a state are grouped into a trailing column
const NO_STATE = "Other";

interface ClinicStateSelectProps {
	clinics: Clinic[];
	selectedIds: number[];
	onChange: (clinicIds: number[]) => void;
	disabled?: boolean;
}

export const ClinicStateSelect = ({
	clinics,
	selectedIds,
	onChange,
	disabled = false
}: ClinicStateSelectProps) => {
	const columns = useMemo(() => {
		const groups = new Map<string, Clinic[]>();
		(clinics || []).forEach((clinic) => {
			const state = clinic.state?.trim().toUpperCase() || NO_STATE;
			const group = groups.get(state);
			group ? group.push(clinic) : groups.set(state, [clinic]);
		});

		return Array.from(groups.entries())
			.sort(([stateA], [stateB]) => {
				if (stateA === NO_STATE) {
					return 1;
				}
				if (stateB === NO_STATE) {
					return -1;
				}
				return stateA.localeCompare(stateB);
			})
			.map(([state, stateClinics]) => ({
				state,
				clinics: [...stateClinics].sort((a, b) => a.name.localeCompare(b.name))
			}));
	}, [clinics]);

	const rowCount = columns.reduce(
		(max, column) => Math.max(max, column.clinics.length),
		0
	);

	const toggleClinic = (clinicId: number) => {
		onChange(
			selectedIds.includes(clinicId)
				? selectedIds.filter((id) => id !== clinicId)
				: [...selectedIds, clinicId]
		);
	};

	const toggleState = (stateClinics: Clinic[], checked: boolean) => {
		const stateIds = stateClinics.map((clinic) => clinic.id);
		onChange(
			checked
				? Array.from(new Set([...selectedIds, ...stateIds]))
				: selectedIds.filter((id) => !stateIds.includes(id))
		);
	};

	if (columns.length === 0) {
		return <></>;
	}

	const labelStyle = {
		display: "flex",
		alignItems: "center",
		gap: 8,
		cursor: disabled ? "default" : "pointer"
	};

	return (
		<table
			aria-label="clinics by state"
			className="ch-table"
			style={{ width: "100%", tableLayout: "fixed" }}
		>
			<thead>
				<tr>
					{columns.map(({ state, clinics: stateClinics }) => {
						const selectedCount = stateClinics.filter((clinic) =>
							selectedIds.includes(clinic.id)
						).length;
						return (
							<th key={state}>
								<label style={labelStyle}>
									<Checkbox
										checked={selectedCount === stateClinics.length}
										indeterminate={
											selectedCount > 0 && selectedCount < stateClinics.length
										}
										disabled={disabled}
										onChange={(e) => toggleState(stateClinics, e.target.checked)}
									/>
									{state}
								</label>
							</th>
						);
					})}
				</tr>
			</thead>
			<tbody>
				{Array.from({ length: rowCount }, (_, rowIndex) => (
					<tr key={rowIndex}>
						{columns.map(({ state, clinics: stateClinics }) => {
							const clinic = stateClinics[rowIndex];
							return (
								<td key={state}>
									{clinic && (
										<label style={labelStyle}>
											<Checkbox
												checked={selectedIds.includes(clinic.id)}
												disabled={disabled}
												onChange={() => toggleClinic(clinic.id)}
											/>
											{clinic.name}
										</label>
									)}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
	);
};
